import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useDashboard(location, fy, dateFrom, dateTo, coinsMode) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [offline, setOffline] = useState(false);
  const [snapshotTs, setSnapshotTs] = useState(null);

  const al = user?.allowed_locations;
  const owner = user ? `${user.email}:${Array.isArray(al) ? al.join("+") : "all"}` : "anon";

  const load = useCallback(async (force = false) => {
    setLoading(true);
    setError(null);
    const snapKey = `opm_snap:${owner}:${location}|${fy}|${dateFrom}|${dateTo}|${coinsMode}`;
    try {
      const res = await axios.get(`${API}/dashboard`, {
        params: {
          location, fy, date_from: dateFrom || "", date_to: dateTo || "", coins: coinsMode || "with",
          ...(force === true ? { force: 1 } : {}),
        },
      });
      setData(res.data);
      setOffline(false);
      setSnapshotTs(null);
      try {
        localStorage.setItem(snapKey, JSON.stringify({ data: res.data, ts: Date.now() }));
      } catch {}
      if (force === true) toast.success("Synced fresh from Google Sheet");
    } catch (e) {
      if ([401, 403].includes(e?.response?.status)) {
        window.location.href = "/login";
        setLoading(false);
        return;
      }
      const raw = localStorage.getItem(snapKey) || localStorage.getItem(`opm_snap:${owner}:all|all|||with`);
      const scoped = Array.isArray(al);
      let snap = null;
      if (raw) {
        try { snap = JSON.parse(raw); } catch {}
        if (snap && scoped && (snap.data?.meta?.locations || []).some((l) => !al.includes(l))) snap = null;
      }
      if (snap) {
        setData(snap.data);
        setSnapshotTs(snap.ts);
        setOffline(true);
      } else {
        setError("Could not load data from the Google Sheet.");
      }
    }
    setLoading(false);
  }, [location, fy, dateFrom, dateTo, coinsMode, owner]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, offline, snapshotTs, refetch: load };
}

export function useRecords(filters) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    axios
      .get(`${API}/records`, { params: filters })
      .then((res) => live && setResult(res.data))
      .catch(() => live && setResult(null))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [filters]);

  return { result, loading };
}
